import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { logUsageEvent } from '../lib/logger';
import { resolverAvisosPendentes, AvisoParaResolucao, ConfirmacaoAviso } from '../lib/resolverAvisosPendentes';
import { avisoAlvejaPerfil } from '../lib/avisoAlvejaPerfil';
import { departamentosAlvejamPerfil } from '../lib/departamentosAlvejamPerfil';
import AvisoSistemaModal, { AvisoSistemaModalAviso } from './AvisoSistemaModal';
import NovidadesTiModal, { NovidadeTiModalItem } from './NovidadesTiModal';

// Gate bloqueante do hub interno para "Avisos do Sistema" — mesmo padrão
// estrutural de ConsumidorPortalGate/ExternoGeradorasGate (App.tsx), um
// wrapper simples em volta do bloco de rotas internas. Só entra em ação pra
// RECIPIENT_ROLES ("todo staff interno" nuclear); parceiro/motoboy/
// geradoras_externo/empresa_parceira_* nunca são bloqueados por avisos do
// sistema (podem ser alvo de um aviso por usuário específico na Fase 2, se
// aparecer necessidade concreta — não nesta fase).
//
// Falha de rede: mesmo padrão fail-open do PortalConsumidorGate — avisa via
// toast e libera o acesso, nunca trava alguém fora do próprio trabalho.

const RECIPIENT_ROLES = ['adm', 'gerente', 'analista', 'vendedor', 'gerente_parcerias'];

interface AvisoRow extends AvisoParaResolucao, AvisoSistemaModalAviso {
    departamento_alvo: string | null;
}

interface NovidadeItemRow {
    id: string;
    titulo: string;
    descricao: string | null;
    categoria: 'novidade' | 'correcao';
    tag: string | null;
    concluido_em: string;
    concluido_por: string | null;
    data_referencia: string;
    departamentos_alvo: string[] | null;
    publicado_em: string | null;
    profiles: { full_name: string } | null;
}

const AvisosSistemaGate = ({ children }: { children: React.ReactNode }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [avisos, setAvisos] = useState<AvisoRow[]>([]);
    const [confirmacoes, setConfirmacoes] = useState<ConfirmacaoAviso[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [dispensados, setDispensados] = useState<Set<string>>(new Set());
    const [novidadesItens, setNovidadesItens] = useState<NovidadeItemRow[]>([]);
    const [ultimaVisualizacao, setUltimaVisualizacao] = useState<string | null>(null);
    const totalInicial = useRef<number | null>(null);

    const elegivel = !!user && !!profile && RECIPIENT_ROLES.includes(profile.role);

    useEffect(() => {
        if (!elegivel || !user) return;
        let cancelado = false;
        (async () => {
            setCarregando(true);
            try {
                const [
                    { data: avisosData, error: errAvisos },
                    { data: confirmData, error: errConfirm },
                    { data: novidadesData, error: errNovidades },
                    { data: visualizacaoData, error: errVisualizacao },
                ] = await Promise.all([
                    supabase.from('avisos_sistema').select('id, titulo, mensagem, modelo, bloqueante, url_anexo, data_inicio, data_fim, departamento_alvo'),
                    supabase.from('avisos_sistema_confirmacoes').select('aviso_id, visualizado_em, confirmado_em').eq('profile_id', user.id),
                    // .eq('status', 'publicado') é obrigatório aqui mesmo com RLS: pra adm/gerente/analista, a
                    // policy "staff_novidades_ti_itens_select" (usada pelo painel de aprovação, sem filtro de
                    // status) é permissiva e se une à policy do destinatário — sem o filtro no client, rascunho
                    // vaza pro modal bloqueante que o próprio staff vê.
                    // Ordenado por publicado_em (não concluido_em): um item rascunho publicado tarde
                    // precisa contar como "novo" a partir de quando ENTROU no feed, não de quando o
                    // chamado foi resolvido. .gte(publicado_em, 7 dias atrás): quem ficou muito tempo sem
                    // logar não precisa "recuperar o atraso" vendo semanas de histórico — só o que é
                    // recente E novo pra ela (ver filtro de ultimaVisualizacao em novidadesAlvejadas).
                    supabase.from('novidades_ti_itens').select('id, titulo, descricao, categoria, tag, concluido_em, concluido_por, data_referencia, departamentos_alvo, publicado_em, profiles(full_name)').eq('status', 'publicado').gte('publicado_em', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()).order('publicado_em', { ascending: false, nullsFirst: false }).limit(100),
                    supabase.from('novidades_ti_visualizacoes').select('ultima_visualizacao_em').eq('profile_id', user.id).maybeSingle(),
                ]);
                if (errAvisos) throw errAvisos;
                if (errConfirm) throw errConfirm;
                if (errNovidades) throw errNovidades;
                if (errVisualizacao) throw errVisualizacao;
                if (cancelado) return;
                setAvisos((avisosData || []) as AvisoRow[]);
                setConfirmacoes((confirmData || []) as ConfirmacaoAviso[]);
                setNovidadesItens((novidadesData || []) as unknown as NovidadeItemRow[]);
                setUltimaVisualizacao(visualizacaoData?.ultima_visualizacao_em ?? null);
            } catch (err) {
                console.error('Erro ao carregar avisos do sistema:', err);
                if (!cancelado) addToast('Não foi possível verificar avisos do sistema agora.', 'error');
            } finally {
                if (!cancelado) setCarregando(false);
            }
        })();
        return () => { cancelado = true; };
        // user.id (não o objeto `user`) de propósito: onAuthStateChange do Supabase troca
        // a referência de `session.user` a cada TOKEN_REFRESHED (refresh periódico e no
        // retorno de foco da aba) mesmo sendo o mesmo usuário. Depender do objeto inteiro
        // refaz o fetch e passa por `carregando` (que libera `children` sem o modal) toda
        // vez que isso acontece — uma janela real, ainda que curta, em que um aviso
        // bloqueante para de bloquear no meio da sessão.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [elegivel, user?.id]);

    const pendentes = useMemo(() => {
        if (!elegivel || !profile) return [];
        const alvejados = avisos.filter((a) => avisoAlvejaPerfil({ departamento_alvo: a.departamento_alvo }, profile));
        const todos = resolverAvisosPendentes(alvejados, confirmacoes, new Date());
        return todos.filter((p) => p.aviso.bloqueante || !dispensados.has(p.aviso.id));
    }, [avisos, confirmacoes, elegivel, profile, dispensados]);

    useEffect(() => {
        if (totalInicial.current === null && !carregando) totalInicial.current = pendentes.length;
    }, [carregando, pendentes.length]);

    // Publicação escolhe alvo por equipe(s) no momento da publicação
    // (NovidadesTiView.tsx) — filtra por alvo, e também tira quem já foi
    // confirmado: uma vez que a pessoa confirmou o feed, só o que foi
    // publicado DEPOIS daquele momento volta a aparecer — ela não precisa
    // rever o que já viu (a query já limitou a janela aos últimos 7 dias).
    // null/vazio em departamentos_alvo continua sendo "todo staff".
    const novidadesAlvejadas = useMemo(() => {
        if (!profile) return [];
        return novidadesItens
            .filter((item) => departamentosAlvejamPerfil(item.departamentos_alvo, profile))
            .filter((item) => !ultimaVisualizacao || (item.publicado_em ?? item.concluido_em) > ultimaVisualizacao);
    }, [novidadesItens, profile, ultimaVisualizacao]);

    // Pendente = sobrou pelo menos um item novo (não visto, publicado nos
    // últimos 7 dias) depois dos filtros acima.
    const novidadesPendente = novidadesAlvejadas.length > 0;

    if (!elegivel || carregando) return <>{children}</>;

    const marcarVisualizado = async (avisoId: string) => {
        if (confirmacoes.some((c) => c.aviso_id === avisoId)) return;
        const agora = new Date().toISOString();
        const { error } = await supabase
            .from('avisos_sistema_confirmacoes')
            .insert({ aviso_id: avisoId, profile_id: user!.id, visualizado_em: agora });
        if (!error) {
            setConfirmacoes((prev) => [...prev, { aviso_id: avisoId, visualizado_em: agora, confirmado_em: null }]);
        } else {
            console.error('Erro ao marcar aviso do sistema como visualizado:', error);
        }
    };

    const confirmarAviso = async (avisoId: string) => {
        const agora = new Date().toISOString();
        const { error } = await supabase
            .from('avisos_sistema_confirmacoes')
            .upsert(
                { aviso_id: avisoId, profile_id: user!.id, confirmado_em: agora },
                { onConflict: 'aviso_id,profile_id' }
            );
        if (!error) {
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'confirmar_aviso_sistema', origem: 'acao' });
            setConfirmacoes((prev) => {
                const jaExiste = prev.some((c) => c.aviso_id === avisoId);
                if (!jaExiste) return [...prev, { aviso_id: avisoId, visualizado_em: agora, confirmado_em: agora }];
                return prev.map((c) => (c.aviso_id === avisoId ? { ...c, confirmado_em: agora } : c));
            });
        } else {
            console.error('Erro ao confirmar aviso do sistema:', error);
            addToast('Não foi possível registrar sua confirmação agora. Tente novamente.', 'error');
        }
    };

    const confirmarNovidades = async () => {
        const agora = new Date().toISOString();
        const { error } = await supabase
            .from('novidades_ti_visualizacoes')
            .upsert({ profile_id: user!.id, ultima_visualizacao_em: agora }, { onConflict: 'profile_id' });
        if (!error) {
            logUsageEvent({ userId: user?.id, modulo: 'config', funcionalidade: 'avisos', acao: 'confirmar_novidades_ti', origem: 'acao' });
            setUltimaVisualizacao(agora);
        } else {
            console.error('Erro ao confirmar novidades TI:', error);
            addToast('Não foi possível registrar sua confirmação agora. Tente novamente.', 'error');
        }
    };

    const avisoAtual = pendentes[0];
    const posicao = totalInicial.current !== null ? totalInicial.current - pendentes.length + 1 : 1;

    // Aviso discreto (risco/novidade) tem prioridade sobre o feed de
    // Novidades TI — se houver os dois pendentes, o feed aparece na próxima
    // vez (não empilha dois modais bloqueantes seguidos na mesma sessão).
    const novidadesItensModal: NovidadeTiModalItem[] = novidadesAlvejadas.map((item) => ({
        id: item.id,
        titulo: item.titulo,
        descricao: item.descricao,
        categoria: item.categoria,
        tag: item.tag,
        concluido_em: item.concluido_em,
        data_referencia: item.data_referencia,
        concluidoPorNome: item.profiles?.full_name,
    }));

    return (
        <>
            {children}
            {avisoAtual && (
                <AvisoSistemaModal
                    aviso={avisoAtual.aviso}
                    posicao={posicao}
                    total={totalInicial.current ?? pendentes.length}
                    onVisualizado={() => marcarVisualizado(avisoAtual.aviso.id)}
                    onConfirmar={() => confirmarAviso(avisoAtual.aviso.id)}
                    onFechar={avisoAtual.aviso.bloqueante ? undefined : () => setDispensados((prev) => new Set(prev).add(avisoAtual.aviso.id))}
                />
            )}
            {!avisoAtual && novidadesPendente && (
                <NovidadesTiModal
                    itens={novidadesItensModal}
                    onVisualizado={() => {}}
                    onConfirmar={confirmarNovidades}
                />
            )}
        </>
    );
};

export default AvisosSistemaGate;
